import { MascotTip } from "@/components/Mascot";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Minus, Plus, PackageCheck, Clock, Trophy } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";

const short = (a) => `${a.slice(0, 6)}…${a.slice(-4)}`;

function Orders({ orders, t, label }) {
  if (!orders.length) return null;
  return (
    <div className="mt-6 space-y-2" data-testid={`${t}-orders`}>
      <p className="text-sm text-slate-300 font-semibold">Your {label} orders</p>
      {orders.map((o) => (
        <div key={o.session_id} data-testid={`${t}-order-${o.session_id}`} className="flex flex-wrap items-center gap-3 text-sm px-3 py-2 rounded-lg bg-slate-900/70 border border-slate-800">
          <span className="text-white">{o.quantity} × {label}</span>
          <span className="font-mono-game text-xs text-slate-400">to {short(o.wallet_address)}</span>
          {o.payment_status === "refunded" ? <span className="ml-auto text-xs text-slate-400">Refunded</span>
            : o.fulfil_status === "sent" ? <span className="ml-auto flex items-center gap-1 text-xs text-emerald-300"><PackageCheck className="w-3.5 h-3.5" />Sent{o.tx_hash ? ` · ${short(o.tx_hash)}` : ""}</span>
              : new Date(o.deliver_by) < new Date()
                ? <span className="ml-auto flex items-center gap-1 text-xs text-red-300"><Clock className="w-3.5 h-3.5" />Delayed, on its way soon</span>
                : <span className="ml-auto flex items-center gap-1 text-xs text-amber-200"><Clock className="w-3.5 h-3.5" />Arriving by {new Date(o.deliver_by).toLocaleString()}</span>}
        </div>
      ))}
    </div>
  );
}

function Jackpot({ d, t }) {
  const w = d.winner;
  return (
    <div data-testid={`${t}-jackpot`} className={`mt-6 rounded-2xl border p-4 ${w ? "border-amber-300/70 bg-amber-400/10 nft-glow" : "border-amber-400/30 bg-slate-900/60"}`}>
      <p className="flex items-center gap-2 text-amber-200 font-semibold text-sm"><Trophy className="w-4 h-4" />
        {w ? "Jackpot drawn!" : `Jackpot: +${d.jackpot.toLocaleString()} credits to one lucky buyer`}</p>
      {w?.withdrawn ? <p className="text-sm text-slate-400 mt-1" data-testid={`${t}-winner-withdrawn`}>The jackpot was withdrawn after the winning order was refunded.</p> : w ? (
        <p className="text-sm text-slate-200 mt-1" data-testid={`${t}-winner`}>
          {w.is_me ? <b className="text-amber-200">You won! +{d.jackpot.toLocaleString()} credits are in your account.</b> : <><b className="text-white">{w.wolf_name}</b> won the +{d.jackpot.toLocaleString()} credit bonus.</>}
          <span className="block text-xs text-slate-500 mt-0.5">Drawn {new Date(w.drawn_at).toLocaleString()}</span>
        </p>
      ) : <p className="text-xs text-slate-400 mt-1" data-testid={`${t}-jackpot-pending`}>Drawn automatically when the last one sells. {d.sold} of {d.total} sold so far.</p>}
    </div>
  );
}

const COPY = {
  pup: { img: "/art/pup_sale.webp", bg: "bg-[#bfe0d3]", kicker: "Limited drop · Polygon NFT", title: "Adopt a Project PUP",
    line: (d) => `A random Project PUPS NFT sent to your wallet within 48 hours. Only ${d.total} available.`,
    boost: "holding one unlocks the PUPS boost (+25% XP & loot, a free daily revive and x2 daily rewards)" },
  exclusive: { img: "/art/exclusive_sale.webp", bg: "bg-[#8fd1c4]", kicker: "Ultra-rare · only 52 exist", title: "Own a Metaland Wolfpack Exclusive",
    line: (d) => `One of the 52 Exclusive wolves, sent to your wallet within 48 hours. Just ${d.total} up for grabs.`,
    boost: "holding one unlocks the top boost tier (+15% Attack, Wolfpack Fury, energy refills twice as fast and x3 daily rewards)" },
  rare: { img: "/art/rare_sale.webp", bg: "bg-[#061016]", kicker: "1 of 1 · only 27 exist · 5 up for grabs · jackpot drop", title: "Win big with a 1 of 1 Rare PUP",
    line: (d) => `One of the 27 hand-made 1 of 1 Project PUPS, picked at random and sent to your wallet within 48 hours. Only ${d.total} up for sale, one per player.`,
    boost: "the moment the last one sells, one buyer is drawn at random to win a further +198,000 credits (297,000 in total). Holding it unlocks the PUPS boost" },
  wolfpack: { img: "/art/wolfpack_sale.webp", bg: "bg-[#0b0d10]", kicker: "Rare · from the 777 collection", title: "Join the Metaland Wolfpack",
    line: (d) => `One of the 777 Metaland Wolfpack wolves, sent to your wallet within 48 hours. Only ${d.total} available, one per player.`,
    boost: "holding one unlocks the Wolfpack boost (Ironhide, bonus XP & loot and x2.5 daily rewards)" },
};

export default function PupSale({ game, drop = "pup" }) {
  const t = drop === "pup" ? "pup" : drop;
  const C = COPY[drop];
  const [d, setD] = useState(null);
  const [qty, setQty] = useState(1);
  const [useMine, setUseMine] = useState(!!game.wallet);
  const [addr, setAddr] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/drops/${drop}/sale`).then(({ data }) => setD(data)).catch(() => {});
  useEffect(() => { load(); }, [drop]); // eslint-disable-line react-hooks/exhaustive-deps
  const waitlist = async (join) => {
    try {
      if (join) await api.post(`/drops/${drop}/waitlist`, { origin_url: window.location.origin });
      else await api.delete(`/drops/${drop}/waitlist`);
      toast.success(join ? "You're on the waitlist. We'll email you when the next drop opens." : "Removed from the waitlist");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!d) return null;

  const max = d.can_buy;
  const n = Math.min(Math.max(1, qty), Math.max(1, max));
  const buy = async () => {
    if (!useMine && !/^0x[0-9a-fA-F]{40}$/.test(addr.trim())) return toast.error("Enter a valid Polygon wallet address (0x followed by 40 characters)");
    setBusy(true);
    try {
      const { data } = await api.post(`/drops/${drop}/checkout`, { quantity: n, use_connected_wallet: useMine, wallet_address: useMine ? null : addr.trim(), origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  return (
    <section data-testid={`${t}-sale`} className={`mb-14 rounded-3xl overflow-hidden border bg-gradient-to-br from-[#0b2230] via-[#081820] to-[#061016] grid md:grid-cols-[minmax(0,420px)_1fr] ${drop === "exclusive" ? "border-amber-300/70 nft-glow" : "border-amber-400/40"}`}>
      <div className={`relative ${C.bg}`}>
        <img src={C.img} alt={C.title} className="w-full h-full object-cover aspect-square" />
        <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-black/70 text-xs font-mono-game text-amber-200" data-testid={`${t}-stock`}>{d.remaining} / {d.total} left</span>
      </div>
      <div className="p-6 sm:p-10">
        <p className="text-xs uppercase tracking-[0.3em] font-mono-game text-amber-400/90 mb-3">{C.kicker}</p>
        <h2 className="font-display text-3xl sm:text-4xl text-white mb-3">{C.title}</h2>
        <p className="text-slate-300 mb-1">{C.line(d)}</p>
        <p className="text-slate-400 text-sm mb-6">Each one also gives you <b className="text-amber-200" data-testid={`${t}-bonus`}>+{d.bonus_credits.toLocaleString()} game credits</b> straight away, and {C.boost}.</p>
        <div className="flex items-end gap-6 mb-6">
          <div><p className="font-display text-4xl text-amber-200" data-testid={`${t}-price`}>${d.price.toFixed(2)}</p><p className="text-xs text-slate-500">each · max {d.max_per_person} per person</p></div>
          {d.on_sale && max > 0 && (
            <div className="flex items-center gap-2" data-testid={`${t}-qty`}>
              <button data-testid={`${t}-qty-minus`} onClick={() => setQty(Math.max(1, n - 1))} className="p-2 rounded-full border border-slate-600 hover:border-fuchsia-500" aria-label="Fewer"><Minus className="w-4 h-4" /></button>
              <span className="w-8 text-center font-mono-game text-xl" data-testid={`${t}-qty-value`}>{n}</span>
              <button data-testid={`${t}-qty-plus`} onClick={() => setQty(Math.min(max, n + 1))} className="p-2 rounded-full border border-slate-600 hover:border-fuchsia-500" aria-label="More"><Plus className="w-4 h-4" /></button>
            </div>
          )}
        </div>
        {!d.on_sale ? (
          <div className="space-y-4">
            <button data-testid={`${t}-coming-soon`} disabled className="ember-btn w-full max-w-md py-4 rounded-full text-sm opacity-50 cursor-not-allowed">COMING SOON</button>
            <MascotTip testid={`${t}-mascot-tip`}>This drop isn't open yet. Check back soon, there won't be many to go round.</MascotTip>
          </div>
        ) : max > 0 ? (
          <div className="space-y-3 max-w-md">
            {game.wallet && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" data-testid={`${t}-use-wallet-checkbox`} checked={useMine} onChange={(e) => setUseMine(e.target.checked)} className="accent-fuchsia-500 w-4 h-4" />
                Send to my connected wallet <span className="font-mono-game text-xs text-slate-400">{short(game.wallet.address)}</span>
              </label>
            )}
            {!useMine && <Input data-testid={`${t}-wallet-input`} value={addr} onChange={(e) => setAddr(e.target.value)} placeholder="Polygon wallet address (0x…)" className="bg-slate-900 font-mono-game text-sm" />}
            <button data-testid={`${t}-buy-btn`} disabled={busy} onClick={buy} className="ember-btn w-full py-4 rounded-full text-sm">
              {busy ? "OPENING CHECKOUT…" : `BUY ${n} · $${(d.price * n).toFixed(2)}`}
            </button>
            <p className="text-[11px] text-slate-500">You'll get +{(d.bonus_credits * n).toLocaleString()} credits as soon as payment clears. Double-check the address: NFTs sent to a wrong address can't be recovered.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <p data-testid={`${t}-unavailable`} className="text-slate-300">{d.remaining === 0 ? "Sold out. Thanks, pack!" : `You've reached the limit of ${d.max_per_person} per person.`}</p>
            {d.remaining === 0 && !d.jackpot && (d.on_waitlist
              ? <p className="text-sm text-emerald-300" data-testid={`${t}-on-waitlist`}>You're on the waitlist. We'll email you when the next drop opens. <button onClick={() => waitlist(false)} className="text-slate-400 underline ml-1" data-testid={`${t}-leave-waitlist`}>Leave</button></p>
              : <button data-testid={`${t}-join-waitlist`} onClick={() => waitlist(true)} className="px-6 py-3 rounded-full border border-amber-400/60 text-amber-200 text-sm hover:bg-amber-500/10">Email me when the next drop opens</button>)}
          </div>
        )}
        {d.jackpot && <Jackpot d={d} t={t} />}
        <Orders orders={d.orders} t={t} label={{ pup: "pup", exclusive: "Exclusive wolf", wolfpack: "Wolfpack wolf", rare: "1 of 1 Rare PUP" }[drop]} />
      </div>
    </section>
  );
}
